import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import ReactGA from "react-ga4";

// Initialize GA4
const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID || "G-XXXXXXXXXX";
ReactGA.initialize(GA_TRACKING_ID);

// Pages
import Home from "@/pages/Home";
import TripDetail from "@/pages/TripDetail";
import TripCategory from "@/pages/TripCategory";
import ViajesDestacados from "@/pages/ViajesDestacados";
import SobreNosotros from "@/pages/SobreNosotros";
import Contacto from "@/pages/Contacto";
import ServiciosEmpresas from "@/pages/ServiciosEmpresas";
import Blog from "@/pages/Blog";
import BlogPostDetail from "@/pages/BlogPostDetail";
import GuiasPersonalizadas from "@/pages/GuiasPersonalizadas";
import Destinations from "@/pages/Destinations";
import DestinationLanding from "@/pages/DestinationLanding";
import RecursosGratuitos from "@/pages/RecursosGratuitos";
import ScrollToTop from "@/components/layout/ScrollToTop";
import CookieBanner from "@/components/layout/CookieBanner";

// Admin
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTrips from "@/pages/admin/AdminTrips";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminChat from "@/pages/admin/AdminChat";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminDestinations from "@/pages/admin/AdminDestinations";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminPages from "@/pages/admin/AdminPages";
import AdminLogs from "@/pages/admin/AdminLogs";
import AdminSystem from "@/pages/admin/AdminSystem";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios Interceptor para manejar tokens expirados o inválidos
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Si el backend devuelve 401, limpiamos el storage y redirigimos
      if (window.location.pathname.startsWith('/gestion-agencia') && window.location.pathname !== '/gestion-agencia') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/gestion-agencia';
      }
    }
    return Promise.reject(error);
  }
);

// Initialize data on first load
const initializeData = async () => {
  try {
    await axios.post(`${API}/init-data`);
  } catch (e) {
    console.log("Data already initialized or error:", e.message);
  }
};

// Generar o recuperar session_id para tracking coherente
const getSessionId = () => {
  let sid = sessionStorage.getItem('vde_session_id');
  if (!sid) {
    sid = Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('vde_session_id', sid);
  }
  return sid;
};

// Detectar entorno actual
const getEnv = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'DEV';
  if (hostname.startsWith('ocu.')) return 'OCU';
  return 'REAL';
};

function PageViewTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Google Analytics
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
    
    const sessionId = getSessionId();
    const env = getEnv();
    
    // Tracking interno
    const trackVisit = async () => {
      try {
        await axios.post(`${API}/track-visit`, {
          path: location.pathname,
          timestamp: new Date().toISOString(),
          session_id: sessionId,
          env: env
        });
      } catch (e) {
        console.warn("Tracking failed:", e.message);
      }
    };
    
    trackVisit();

    // Heartbeat para duración (cada 10 segundos)
    const heartbeatInterval = setInterval(async () => {
      try {
        await axios.post(`${API}/track-heartbeat`, {
          session_id: sessionId,
          path: location.pathname,
          seconds: 10
        });
      } catch (e) {
        // Silently ignore heartbeat errors
      }
    }, 10000);

    return () => clearInterval(heartbeatInterval);
  }, [location]);
  return null;
}

function App() {
  useEffect(() => {
    initializeData();
  }, []);

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <ScrollToTop />
        <CookieBanner />
        <PageViewTracker />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/viaje/:tripId" element={<TripDetail />} />
          <Route path="/viajes" element={<ViajesDestacados />} />
          <Route path="/tipos-viajes/:category" element={<TripCategory />} />
          <Route path="/sobre-nosotros" element={<SobreNosotros />} />
          <Route path="/servicios-empresas" element={<ServiciosEmpresas />} />
          <Route path="/contacto" element={<Contacto />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPostDetail />} />
          <Route path="/guias-personalizadas" element={<GuiasPersonalizadas />} />          <Route path="/destinos" element={<Destinations />} />          <Route path="/destinos/:slug" element={<DestinationLanding />} />
          <Route path="/recursos-gratuitos" element={<RecursosGratuitos />} />
          
          {/* Admin Routes */}
          <Route path="/gestion-agencia" element={<AdminLogin />} />
          
          {/* Protected Admin Routes */}
          <Route path="/gestion-agencia/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/gestion-agencia/viajes" element={<ProtectedRoute><AdminTrips /></ProtectedRoute>} />
          <Route path="/gestion-agencia/destinos" element={<ProtectedRoute><AdminDestinations /></ProtectedRoute>} />
          <Route path="/gestion-agencia/reservas" element={<ProtectedRoute><AdminBookings /></ProtectedRoute>} />
          <Route path="/gestion-agencia/calendario" element={<ProtectedRoute><AdminCalendar /></ProtectedRoute>} />
          <Route path="/gestion-agencia/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
          <Route path="/gestion-agencia/newsletter" element={<ProtectedRoute><AdminNewsletter /></ProtectedRoute>} />
          <Route path="/gestion-agencia/mensajes" element={<ProtectedRoute><AdminMessages /></ProtectedRoute>} />
          <Route path="/gestion-agencia/chat" element={<ProtectedRoute><AdminChat /></ProtectedRoute>} />
          <Route path="/gestion-agencia/media" element={<ProtectedRoute><AdminMedia /></ProtectedRoute>} />
          <Route path="/gestion-agencia/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
          <Route path="/gestion-agencia/testimonios" element={<ProtectedRoute><AdminTestimonials /></ProtectedRoute>} />
          <Route path="/gestion-agencia/paginas" element={<ProtectedRoute><AdminPages /></ProtectedRoute>} />
          <Route path="/gestion-agencia/logs" element={<ProtectedRoute><AdminLogs /></ProtectedRoute>} />
          <Route path="/gestion-agencia/system" element={<ProtectedRoute><AdminSystem /></ProtectedRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
